import { LitElement, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { api, type CodexRunEvent } from "../api/client";
import { i18n } from "../i18n";
import { dateTime } from "../view-model";
import "./app-pagination";

@customElement("codex-run-events")
export class CodexRunEvents extends LitElement {
  @property({ attribute: "run-id" }) runId = "";
  @property({ type: Boolean }) active = false;
  @state() private items: CodexRunEvent[] = [];
  @state() private page = 1;
  @state() private pageSize = 100;
  @state() private total = 0;
  @state() private totalPages = 0;
  @state() private missing = 0;
  @state() private legacy = false;
  @state() private unseen = 0;
  @state() private loading = false;
  @state() private error = "";
  @state() private expanded = new Map<number, boolean>();
  private version = 0;
  private timer?: number;
  private loaded = false;
  private pendingRefresh = false;
  protected createRenderRoot() {
    return this;
  }
  private readonly languageChanged = () => this.requestUpdate();
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
  }
  disconnectedCallback() {
    this.version++;
    window.clearTimeout(this.timer);
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }
  protected updated(changed: PropertyValues<this>) {
    if (changed.has("runId")) {
      this.items = [];
      this.page = 1;
      this.total = 0;
      this.totalPages = 0;
      this.loaded = false;
      this.unseen = 0;
      this.missing = 0;
      this.legacy = false;
      this.error = "";
      this.pendingRefresh = false;
      this.expanded = new Map();
      void this.load(1);
    } else if (changed.has("active")) {
      if (this.loading) this.pendingRefresh = true;
      else void this.load(this.page, true);
    }
  }
  private schedule() {
    window.clearTimeout(this.timer);
    if (this.isConnected && this.active) {
      this.timer = window.setTimeout(
        () => void this.load(this.page, true),
        3000,
      );
    }
  }
  private async load(page = this.page, background = false) {
    if (!this.runId) return;
    const version = ++this.version;
    const id = this.runId;
    const size = this.pageSize;
    window.clearTimeout(this.timer);
    if (!background) this.loading = true;
    this.error = "";
    try {
      const result = await api.codexRunEvents(id, page, size);
      if (version !== this.version || id !== this.runId) return;
      if (background && this.loaded && result.total_count > this.total) {
        this.unseen += result.total_count - this.total;
      } else if (!background) this.unseen = 0;
      this.items = result.items;
      this.page = result.page;
      this.total = result.total_count;
      this.totalPages = result.total_pages;
      this.missing = result.missing_events;
      this.legacy = result.storage === "legacy_snapshot";
      this.loaded = true;
    } catch (error) {
      if (version === this.version) this.error = (error as Error).message;
    } finally {
      if (version === this.version) {
        this.loading = false;
        if (this.pendingRefresh) {
          this.pendingRefresh = false;
          void this.load(this.page, true);
        } else this.schedule();
      }
    }
  }
  private changeSize(value: string) {
    const size = Number(value);
    if (![10, 20, 50, 100].includes(size)) return;
    this.pageSize = size;
    void this.load(1);
  }
  private eventOpen(item: CodexRunEvent) {
    const manual = this.expanded.get(item.sequence);
    if (manual !== undefined) return manual;
    return item.sequence === this.items.at(-1)?.sequence;
  }
  private toggleEvent(item: CodexRunEvent, event: Event) {
    event.preventDefault();
    const details = (event.currentTarget as HTMLElement).closest("details");
    this.expanded = new Map(this.expanded).set(item.sequence, !details?.open);
  }
  render() {
    return html`<section class="output-section codex-event-section">
      <strong>${i18n.t("codexProgressTitle")}</strong>
      <div class="toolbar source-history-toolbar">
        <label class="native-field"
          ><span>${i18n.t("codexHistoryPageSize")}</span>
          <select
            class="a2k-native-input"
            .value=${String(this.pageSize)}
            @change=${(event: Event) => this.changeSize((event.target as HTMLSelectElement).value)}
          >
            ${[10, 20, 50, 100].map((size) => html`<option value=${size} ?selected=${size === this.pageSize}>${size}</option>`)}
          </select>
        </label>
        <span role="status"
          >${i18n.t("codexEventsTotal", { count: this.total })}</span
        >
        <a2k-button
          ?disabled=${this.loading}
          @click=${() => void this.load(this.page)}
          >${i18n.t("refresh")}</a2k-button
        >
        <a2k-button
          ?disabled=${this.loading || !this.totalPages}
          @click=${() => void this.load(this.totalPages)}
          >${i18n.t("codexEventsLatest")}</a2k-button
        >
      </div>
      ${this.unseen ? html`<p role="status">${i18n.t("codexEventsNew", { count: this.unseen })}</p>` : nothing}
      ${this.legacy ? html`<p>${i18n.t("codexEventsLegacy")}</p>` : nothing}
      ${this.missing ? html`<p>${i18n.t("codexEventsMissing", { count: this.missing })}</p>` : nothing}
      ${this.error ? html`<p class="status status-error" role="alert">${this.error}</p>` : nothing}
      <div
        class="codex-events-scroll"
        tabindex="0"
        aria-label=${i18n.t("codexProgressTitle")}
      >
        ${repeat(
          this.items,
          (item) => item.sequence,
          (item) =>
            html`<details class="codex-event-row" .open=${this.eventOpen(item)}>
              <summary
                @click=${(event: Event) => this.toggleEvent(item, event)}
              >
                ${dateTime(item.at)} · ${item.event} · #${item.sequence}
              </summary>
              <pre class="code-output">${item.text}</pre>
              ${item.truncated ? html`<p>${i18n.t("codexEventsTruncated")}</p>` : nothing}
            </details>`,
        )}
        ${!this.items.length ? html`<p>${i18n.t(this.loading ? "loading" : "codexProgressEmpty")}</p>` : nothing}
      </div>
      <app-pagination
        .page=${this.page}
        .totalPages=${this.totalPages}
        .busy=${this.loading}
        @page-change=${(event: CustomEvent<{ page: number }>) => void this.load(event.detail.page)}
      ></app-pagination>
    </section>`;
  }
}
